// Exchanges v2 — MOVEMENTS AND FILLS, BEHIND A TAP. The two secondary lists the old screen kept as tabs open here in the
// kit's DetailSheet (the view is in the URL: Back closes it). The reads are the screen's own (same keys, read only while
// the sheet is open, as the old tabs read only while shown); the rows, the words and the doors are the old screen's:
// a movement is In / Out with the sign, Dubai time, and "Record the move →" when the chain saw the same money; a fill is
// Bought / Sold, the pair, the price, the amount for its cost, the fee; above the fills, one line per day · venue · pair ·
// side with the totals, the cost-weighted rate, the fee and the count — the figures a conversion is booked from.
import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { CexFillRow, CexFillSummary } from '../../../lib/api';
import { fmtAmount, fmtDay, fmtQuantity, fmtRate, fmtStamp, displayAsset } from '../../../lib/format';
import { brandCase } from '../../../lib/naming';
import { Delta } from '../../Ledger';
import { Pill } from '../../kit';
import { DetailSheet, EmptyState, ReadFailed, SkeletonRows } from '../kit';
import { FILLS_DAYS, feeWords, moveMoneyUrl, type MovementRow } from './rules';

interface Read<T> { data?: T; isError: boolean; isPending: boolean; error: unknown; refetch: () => unknown }

/** One record's two lines, as the Register's phone card draws them. */
function Row({ children }: { children: React.ReactNode }) {
  return <li className="border-b border-stroke-soft py-3 last:border-b-0">{children}</li>;
}

export function MovementsSheet({ mv, onClose }: { mv: Read<{ rows: MovementRow[] }>; onClose: () => void }) {
  const rows = mv.data?.rows ?? [];
  return (
    <DetailSheet title="Movements" onClose={onClose} wide>
      {mv.isError && <ReadFailed tone="bad" what="movements" error={mv.error} onRetry={() => mv.refetch()} className="mb-3" />}
      {mv.isPending && <SkeletonRows what="movements" />}
      {mv.data && !mv.isError && rows.length === 0 && <EmptyState>Nothing here.</EmptyState>}
      {rows.length > 0 && (
        <ul>
          {rows.map((m) => {
            const isIn = m.direction === 'in';
            const moveUrl = moveMoneyUrl(m);
            const aside = [m.network, m.status].filter(Boolean).join(' · ');
            return (
              <Row key={m.id}>
                <div className="flex items-start gap-2 text-body">
                  <Pill tone={isIn ? 'good' : 'bad'} className="mt-px shrink-0">{isIn ? 'In' : 'Out'}</Pill>
                  <span className="min-w-0 flex-1 break-words font-medium">{brandCase(m.venue)} · {brandCase(m.label)}</span>
                  <span className="shrink-0 whitespace-nowrap"><Delta value={(isIn ? 1 : -1) * Math.abs(Number(m.amount))} asset={m.asset} code /></span>
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-meta text-tg-hint">
                  <span className="font-mono tabular">{fmtStamp(m.occurred_at)}</span>
                  {aside && <span>{aside}</span>}
                  {/* One tap to book the move when the chain saw the same money; nothing at all when it did not. */}
                  {moveUrl && <Link className="btn-link text-meta ml-auto whitespace-nowrap max-lg:py-3.5 max-lg:-my-3.5" to={moveUrl}>Record the move →</Link>}
                </div>
              </Row>
            );
          })}
        </ul>
      )}
    </DetailSheet>
  );
}

/** A fill or a day's sum of fills, as the two registers draw it — one fact per column (his "one fact per place", 1 Nov 2026:
 *  each fill was prose — "side · venue · pair" over a line packing the time, the quantity, its cost and the fee). */
interface FillLine {
  key: string; at: string; dayOnly: boolean; side: 'buy' | 'sell'; venue: string; base: string; quote: string;
  amount: number | string; cost: number | string; rate: number | string | null; fee: string; fills?: number;
}

const Num = ({ v, asset, q = false }: { v: number | string; asset: string; q?: boolean }) => (
  <span className="whitespace-nowrap"><span className="amt">{q ? fmtQuantity(v, asset) : fmtAmount(v, asset)}</span> <span className="text-meta text-tg-hint">{displayAsset(asset)}</span></span>
);
const When = ({ l }: { l: FillLine }) => <span className="whitespace-nowrap font-mono tabular">{l.dayOnly ? fmtDay(l.at) : fmtStamp(l.at)}</span>;
const What = ({ l }: { l: FillLine }) => (
  <span className="block min-w-0">
    <span className="flex flex-wrap items-center gap-x-2 gap-y-1"><Pill tone="info" className="shrink-0">{l.side === 'buy' ? 'Bought' : 'Sold'}</Pill><span className="whitespace-nowrap font-medium">{l.base} · {l.quote}</span></span>
    <span className="mt-0.5 block truncate text-meta font-normal text-tg-hint">{brandCase(l.venue)}{l.fills !== undefined ? ` · ${l.fills} fill${l.fills === 1 ? '' : 's'}` : ''}</span>
  </span>
);

/** ONE FILL, ONE BLOCK — the sheet is 720 px, too narrow for six columns (the pair and the rate were cut): the when, the
 *  fill and its quantity on the first line, then Amount · Rate · Fee as three labelled cells, each fact in its own seat. */
function FillList({ label, rows }: { label: string; rows: FillLine[] }) {
  // On the phone a labelled line each (label left, figure right); from sm three labelled cells. The when, the fill and its
  // quantity keep one place on every row (Astra, 1 Nov 2026).
  const cell = (head: string, v: React.ReactNode) => (
    <span className="flex min-w-0 items-baseline justify-between gap-3 sm:block">
      <span className="text-meta text-tg-hint sm:block sm:text-label sm:font-medium sm:uppercase sm:tracking-wider">{head}</span>
      <span className="text-right text-body sm:mt-0.5 sm:block sm:text-left">{v}</span>
    </span>
  );
  return (
    <ul aria-label={label} className="rounded-[10px] border border-stroke">
      {rows.map((l) => (
        <li key={l.key} className="border-b border-stroke-soft px-4 py-3 last:border-b-0">
          <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-x-3 gap-y-1 sm:grid-cols-[6.5rem_minmax(0,1fr)_auto]">
            <span className="pt-0.5 text-meta text-tg-hint"><When l={l} /></span>
            <span className="col-span-2 row-start-2 sm:col-span-1 sm:col-start-2 sm:row-start-1"><What l={l} /></span>
            <span className="col-start-2 row-start-1 text-right text-body sm:col-start-3"><Num v={l.amount} asset={l.base} q /></span>
          </div>
          <div className="mt-2 space-y-1 sm:grid sm:grid-cols-3 sm:gap-x-3 sm:space-y-0 sm:pl-[calc(6.5rem+0.75rem)]">
            {cell('Amount', <Num v={l.cost} asset={l.quote} />)}
            {cell('Rate', <span className="amt whitespace-nowrap">{l.rate == null ? '—' : fmtRate(l.rate, l.quote)}</span>)}
            {cell('Fee', <span className="whitespace-nowrap">{l.fee}</span>)}
          </div>
        </li>
      ))}
    </ul>
  );
}

export function FillsSheet({ fl, onClose }: {
  fl: Read<{ rows: CexFillRow[]; summary: CexFillSummary[] }>;
  onClose: () => void;
}) {
  const d = fl.data;
  const days: FillLine[] = (d?.summary ?? []).map((g) => {
    const [base = '', quote = ''] = g.symbol.split('/');
    return { key: `${g.day}|${g.venue}|${g.symbol}|${g.side}`, at: g.first_at ?? `${g.day}T12:00:00+04:00`, dayOnly: true, side: g.side,
      venue: g.venue, base, quote, amount: g.amount, cost: g.cost, rate: g.rate, fee: feeWords(g.fees), fills: g.fills };
  });
  const fills: FillLine[] = (d?.rows ?? []).map((r) => ({
    key: String(r.id), at: r.occurred_at, dayOnly: false, side: r.side, venue: r.venue, base: r.base_asset, quote: r.quote_asset,
    amount: r.amount, cost: r.cost, rate: r.price,
    fee: r.fee_amount != null && r.fee_asset ? `${fmtQuantity(r.fee_amount, r.fee_asset)} ${displayAsset(r.fee_asset)}` : '—',
  }));
  // ONE LEVEL AT A TIME (Astra, 1 Nov 2026: the days and then every fill, both open, buried the fills): By day — the figures a
  // conversion is booked from — or Each fill.
  const [level, setLevel] = useState<'days' | 'fills'>('days');
  const showDays = level === 'days' && days.length > 0;
  return (
    <DetailSheet title="Fills" onClose={onClose} wide>
      {days.length > 0 && fills.length > 0 && (
        <div className="mb-4 flex gap-2" role="group" aria-label="Show">
          <button type="button" aria-pressed={level === 'days'} onClick={() => setLevel('days')} className={`chip max-lg:min-h-11 ${level === 'days' ? 'chip-on' : ''}`}>By day</button>
          <button type="button" aria-pressed={level === 'fills'} onClick={() => setLevel('fills')} className={`chip max-lg:min-h-11 ${level === 'fills' ? 'chip-on' : ''}`}>Each fill</button>
        </div>
      )}
      {fl.isError && <ReadFailed tone="bad" what="fills" error={fl.error} onRetry={() => fl.refetch()} className="mb-3" />}
      {fl.isPending && <SkeletonRows what="fills" />}
      {showDays && (
        <section className="mb-5">
          <h3 className="mb-2 text-label font-medium uppercase tracking-wider text-tg-hint">Last {FILLS_DAYS} days, by day</h3>
          <FillList label={`Fills by day, last ${FILLS_DAYS} days`} rows={days} />
        </section>
      )}
      {d && !fl.isError && fills.length === 0 && <EmptyState>Nothing here.</EmptyState>}
      {fills.length > 0 && !showDays && (
        <section>
          <h3 className="mb-2 text-label font-medium uppercase tracking-wider text-tg-hint">Each fill</h3>
          <FillList label="Fills" rows={fills} />
        </section>
      )}
    </DetailSheet>
  );
}
